import type { LiveConsolePage } from "@/@types/live";
import { LIVE_PATH } from "@/constants/appChrome";
import type { MsgKey } from "@/i18n/strings";

/** 顶部单个功能页 */
export interface ConsolePageTab {
  kind: "page";
  /** BytePlus type */
  page: LiveConsolePage;
  /** 页签文案 */
  label: MsgKey;
}

/** 模型训练：同一个页签下挂虚拟形象和语音 */
export interface ConsoleMenuTab {
  kind: "menu";
  /** 页签文案 */
  label: MsgKey;
  /** 下拉项 */
  items: { page: LiveConsolePage; label: MsgKey }[];
}

/** 控制台顶部入口 */
export type ConsoleTab = ConsolePageTab | ConsoleMenuTab;

/** 文档支持的功能页，顺序即页签顺序 */
export const CONSOLE_TABS: ConsoleTab[] = [
  { kind: "page", page: "AILiveRoom", label: "liveConsoleRoom" },
  {
    kind: "menu",
    label: "liveConsoleTraining",
    items: [
      { page: "AILiveAvatarTraining", label: "liveConsoleAvatar" },
      { page: "AILiveVoiceTraining", label: "liveConsoleVoice" },
    ],
  },
  { kind: "page", page: "AILiveProducts", label: "liveConsoleProducts" },
  { kind: "page", page: "AILiveMaterials", label: "liveConsoleMaterials" },
  { kind: "page", page: "AILiveKnowledgeBase", label: "liveConsoleKnowledge" },
  { kind: "page", page: "AILiveServiceCenter", label: "liveConsoleService" },
];

/** 只给 BytePlus 主账号看的功能页 */
const MAIN_ACCOUNT_PAGES = new Set<LiveConsolePage>(["AILiveServiceCenter"]);

/** 这个功能页是否只给 BytePlus 主账号 */
export function consolePageRequiresMainAccount(page: LiveConsolePage): boolean {
  return MAIN_ACCOUNT_PAGES.has(page);
}

/**
 * 当前用户能看到的页签。
 * 服务中心只在主账号时保留，其余页签不变。
 */
export function visibleConsoleTabs(mainAccount: boolean): ConsoleTab[] {
  if (mainAccount) return CONSOLE_TABS;
  return CONSOLE_TABS.filter((tab) => tab.kind !== "page" || !MAIN_ACCOUNT_PAGES.has(tab.page));
}

/** 二级路由路径段，挂在 /ai-live 下面 */
const PAGE_SLUG: Record<LiveConsolePage, string> = {
  AILiveRoom: "room",
  AILiveAvatarTraining: "avatar",
  AILiveVoiceTraining: "voice",
  AILiveProducts: "products",
  AILiveMaterials: "materials",
  AILiveKnowledgeBase: "knowledge",
  AILiveServiceCenter: "service",
};

/** 按页签顺序展开的功能页，用来注册二级路由 */
export const CONSOLE_ROUTE_PAGES: LiveConsolePage[] = [
  "AILiveRoom",
  "AILiveAvatarTraining",
  "AILiveVoiceTraining",
  "AILiveProducts",
  "AILiveMaterials",
  "AILiveKnowledgeBase",
  "AILiveServiceCenter",
];

/** 二级路由路径段，例如 room */
export function consolePageSlug(page: LiveConsolePage): string {
  return PAGE_SLUG[page];
}

/** 功能页对应的二级路由，例如 /ai-live/room */
export function consolePagePath(page: LiveConsolePage): string {
  return `${LIVE_PATH}/${consolePageSlug(page)}`;
}

/** 二级路由路径段还原成 BytePlus type */
export function consolePageBySlug(slug: string): LiveConsolePage | undefined {
  return CONSOLE_ROUTE_PAGES.find((page) => PAGE_SLUG[page] === slug);
}

const PAGE_LABEL: Record<LiveConsolePage, MsgKey> = {
  AILiveRoom: "liveConsoleRoom",
  AILiveAvatarTraining: "liveConsoleAvatar",
  AILiveVoiceTraining: "liveConsoleVoice",
  AILiveProducts: "liveConsoleProducts",
  AILiveMaterials: "liveConsoleMaterials",
  AILiveKnowledgeBase: "liveConsoleKnowledge",
  AILiveServiceCenter: "liveConsoleService",
};

/** 当前功能页的页签文案 */
export function consolePageLabel(page: LiveConsolePage): MsgKey {
  return PAGE_LABEL[page];
}

/**
 * 换成另一个功能页。
 * LoginLivesaasToken 已经编码过，只改 type，不重新解析整条 query。
 */
export function withConsoleType(rawUrl: string, page: LiveConsolePage): string {
  const hashAt = rawUrl.indexOf("#");
  const withoutHash = hashAt >= 0 ? rawUrl.slice(0, hashAt) : rawUrl;
  const hash = hashAt >= 0 ? rawUrl.slice(hashAt) : "";
  const queryAt = withoutHash.indexOf("?");
  if (queryAt < 0) {
    return `${withoutHash}?type=${page}${hash}`;
  }
  const base = withoutHash.slice(0, queryAt);
  let replaced = false;
  const parts = withoutHash
    .slice(queryAt + 1)
    .split("&")
    .filter((part) => part.length > 0)
    .map((part) => {
      const eq = part.indexOf("=");
      const key = eq >= 0 ? part.slice(0, eq) : part;
      if (key !== "type") return part;
      replaced = true;
      return `type=${page}`;
    });
  if (!replaced) parts.push(`type=${page}`);
  return `${base}?${parts.join("&")}${hash}`;
}
